import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import { Box, Grid, KeyValue, SectionHeader } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useParams } from 'react-router-dom';
import {
  DownloadVerificationSlipResponse,
  useGetDownloadVerificationSlip,
} from '../../../api-refactored/group-exam-registration/useGetDownloadVerificationSlip';
import DownloadFromS3Action from '../../../components/buttons/downloadFromS3Action';
import { RetrieveS3URLResponse } from '../../../api/document/files/api-services/model';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';

const useStyles = makeStyles()((theme) =>
({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
}));

const ReviewBaseInfo = ({ reviewBaseInfo }: any) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();

  const [fileUuid, setFileUuid] = useState<string>('');
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );

  const { refetch: refetchDownloadVerificationSlip } = useGetDownloadVerificationSlip({
    uuid,
    onSuccess: (res: DownloadVerificationSlipResponse) => {
      setFileUuid(res.uuid);
    },
  });

  const downloadPDF = useCallback((res: RetrieveS3URLResponse) => {
    if (res.fileStatus === FileUploadState.SUCCESS) {
      // only download once, disable download continue pooling
      setFileUuid('');
    }
    setFileDownloadStatus(res.fileStatus as FileUploadState);
  }, []);

  const downloadVerificationSlip = () => {
    setFileDownloadStatus(FileUploadState.UPLOADING);
    refetchDownloadVerificationSlip();
  };

  const handleCloseDownloadErrorPrompt = () => {
    setFileDownloadStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {reviewBaseInfo && (
        <>
          <Box>
            <SectionHeader
              title={t('exam->>ReviewExam.ReviewRegistration')}
            >
              <DownloadFromS3Action
                  isLoading={fileDownloadStatus === 'UPLOADING'}
                  color="secondary"
                  actionType="link"
                  requestRoute={{ data: fileUuid || '', url: '/api/s3/files' }}
                  label={t('exam->>ReviewExam.DownloadVerificationSlip')}
                  onClick={downloadVerificationSlip}
                  onDownload={downloadPDF}
                  isDisplayDownloadLabelWithIcon
              />
            </SectionHeader>
            <KeyValue
              keyText={t('exam->>RegistrationSummary.ApplicationId')}
              valueText={reviewBaseInfo.applicationId}
            />
            <KeyValue
              keyText={t('exam->>RegistrationSummary.Coordinator')}
              valueText={reviewBaseInfo.coordinatorName}
              noPadding
            />
          </Box>
          <Divider className={classes.divider} />
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="examYear"
                keyText={t('exam->>RegistrationSummary.ExamYear')}
                valueText={reviewBaseInfo.examYear.toString()}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="examLevel"
                keyText={t('exam->>RegistrationSummary.ExamLevel')}
                valueText={reviewBaseInfo.examLevel.join(', ')}
              />
            </Grid>
          </Grid>
          {fileDownloadStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </>
      )}
    </>
  );
};

export default ReviewBaseInfo;
